//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext, useEffect } from 'react';
import { makeStyles, CircularProgress } from '@material-ui/core';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Card from 'react-credit-cards';
//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { PLACEHOLDER_FIELD_NAME } from '../../common/placeholderHelper';
import { createCardSetupIntent, retrieveStripeCustomer } from '../../common/services/paymentServices';
import { getConfirmCardSetupBillingDetails } from '../../common/paymentHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveEmployerSetupIntentCardPaymentMethod } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
// import AfitdNumberFormat from '../../common/elemental/AfitdNumberFormat';
// import AfitdDatePicker from '../../common/elemental/AfitdDatePicker';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
// import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';
import _some from 'lodash/some';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    // const { } = params;

    const errorMessageStyles = { color: 'red' };

    return makeStyles(theme => ({
        inputBoxContainer: { display: 'flex' },
        inputBox: { flex: 1, padding: '0 5px', marginTop: 10 },
        inline: { display: 'flex', alignItems: 'center' },

        title: { fontWeight: 'bold', margin: '10px 0' },
        existingCardInfoContainer: { borderRadius: 4, border: '2px solid rgb(213,215,221)', padding: 20, margin: '5px 0' },
        cardInfoContainer: { borderRadius: 4, border: '2px solid rgb(213,215,221)', padding: 20, margin: '5px 0' },
        label: { margin: '10px 0 5px 0', fontWeight: 'bold' },

        stripeCardNumberErrorMessage: errorMessageStyles,
        stripeCardExpiryErrorMessage: errorMessageStyles,
        stripeCardCvcErrorMessage: errorMessageStyles,

        buttonBox: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '30px 0 0 0',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
        },
        cancelButton: { marginRight: 16, [theme.breakpoints.down('sm')]: { marginRight: 0, marginBottom: 16 } },
        saveCardButton: { [theme.breakpoints.down('sm')]: { marginBottom: 16 } },
        circularDiv: {
            textAlign: 'center',
            padding: 20,
        },
        updateCardDiv: {
            textAlign: 'left',
            marginLeft: 'auto',
        },
        stripeCardElementDiv: {
            border: '2px solid lightgrey',
            padding: '15px',
            borderRadius: '5px',
        },
    }));
};

//---------------------------------------------------------------------
// Helper function
//---------------------------------------------------------------------
// // will format the expiry date
// function limit(val, max) {
//     if (val.length === 1 && val[0] > max[0]) val = '0' + val;

//     if (val.length === 2) {
//         if (Number(val) === 0) val = '01';
//         else if (val > max) val = max;
//     }
//     return val;
// }

// function checkCardExpiryDate(val) {
//     const month = limit(val.substring(0, 2), '12');
//     const year = val.substring(2, 4);

//     return month + (year.length ? '/' + year : '');
// }

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        data,
        // setData,
        stripeComplete,
        stripeErrorMessages,
        handleChangeFields,
        handleClickCancelButton,
        handleClickSaveCardButton,
        stripeBillingDetails,
        stripeCard,
        handleClickUpdateCardLink,
        handleChangeStripeCardNumber,
        handleChangeStripeCardExpiry,
        handleChangeStripeCardCvc,
        isUpdatingCard,
    } = params;
    const { cardHoldersName } = data;

    // TEMP TEMP TEMP TEMP TEMP
    // TEMP TEMP TEMP TEMP TEMP
    // TEMP TEMP TEMP TEMP TEMP
    // TEMP TEMP TEMP TEMP TEMP
    // TEMP TEMP TEMP TEMP TEMP
    // const isDirty = !Object.keys({ ...data }).every(key => !_isEqual(data[key], ''));
    // const isDirty = false;
    // TEMP TEMP TEMP TEMP TEMP
    // TEMP TEMP TEMP TEMP TEMP
    // TEMP TEMP TEMP TEMP TEMP
    // TEMP TEMP TEMP TEMP TEMP
    // TEMP TEMP TEMP TEMP TEMP

    const existingCardHoldersName = stripeBillingDetails?.name || '';
    const existingCardNumber = `${stripeCard?.brand} ending in ${stripeCard?.last4}`;
    const existingCardExpiryDate = `${stripeCard?.exp_month}/${stripeCard?.exp_year}`;

    const isPageValid = getIsOkToSave({ data, stripeComplete, stripeErrorMessages });

    const defns = {
        title: { variant: 'body1', value: 'or enter card info' },
        label: { variant: 'body2' },

        cardHoldersName: { onChange: handleChangeFields, name: 'cardHoldersName', value: cardHoldersName, placeholder: PLACEHOLDER_FIELD_NAME, inputProps: { maxLength: 24 } },
        existingCardHoldersName: { value: existingCardHoldersName },
        existingCardNumber: { value: existingCardNumber },
        existingCardExpiryDate: { value: existingCardExpiryDate },

        updateCardLink: { onClick: handleClickUpdateCardLink, color: 'primary' },

        stripeCardNumber: { onChange: handleChangeStripeCardNumber },
        stripeCardExpiry: { onChange: handleChangeStripeCardExpiry },
        stripeCardCvc: { onChange: handleChangeStripeCardCvc },

        stripeCardNumberErrorMessage: { variant: 'subtitle', value: stripeErrorMessages?.cardNumber },
        stripeCardExpiryErrorMessage: { variant: 'subtitle', value: stripeErrorMessages?.cardExpiry },
        stripeCardCvcErrorMessage: { variant: 'subtitle', value: stripeErrorMessages?.cardCvc },

        cancelButton: { label: 'Cancel', disabled: isUpdatingCard, onClick: handleClickCancelButton },
        saveCardButton: {
            label: 'Save Card',
            isHidden: false,
            disabled: !isPageValid || isUpdatingCard,
            color: 'primary',
            onClick: handleClickSaveCardButton,
            loading: isUpdatingCard,
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const initData = { cardHoldersName: '' };
const initStripeData = { cardNumber: null, cardExpiry: null, cardCvc: null };
const initStripeErrorMessages = { cardNumber: null, cardExpiry: null, cardCvc: null };

const getIsOkToSave = ({ data, stripeComplete, stripeErrorMessages }) => {
    const fieldNames = ['cardHoldersName'];
    const stripeFieldNames = ['cardNumber', 'cardExpiry', 'cardCvc'];
    const hasError = _some(stripeFieldNames, fieldName => stripeErrorMessages[fieldName]);
    const hasIncompleteStripeField = _some(stripeFieldNames, fieldName => !stripeComplete[fieldName]);
    const hasBlankField = _some(fieldNames, fieldName => !data[fieldName]);

    return !hasError && !hasIncompleteStripeField && !hasBlankField;
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const BillingInfo = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { employerId, currentEmployer } = appContext;
    const stripeCusId = currentEmployer?.stripeCusId;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    // Initialize an instance of stripe.
    const stripe = useStripe();
    const stripeElements = useElements();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [data, setData] = useState(initData);
    const [stripeComplete, setStripeComplete] = useState(initStripeData);
    const [stripeErrorMessages, setStripeErrorMessages] = useState(initStripeErrorMessages);
    const [stripeCustomer, setStripeCustomer] = useState();
    const [isRequestLoadCustomer, setIsRequestLoadCustomer] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isUpdatingCard, setIsUpdatingCard] = useState(false);
    // const [stripeCardNumberErrorMessage, setStripeCardNumberErrorMessage] = useState();
    // const [stripeCardExpiryErrorMessage, setStripeCardExpiryErrorMessage] = useState();
    // const [stripeCardCvcErrorMessage, setStripeCardCvcErrorMessage] = useState();

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const stripeBillingDetails = stripeCustomer?.invoice_settings?.default_payment_method?.billing_details;
    const stripeCard = stripeCustomer?.invoice_settings?.default_payment_method?.card || stripeCustomer?.default_source;

    const existingCardHoldersName = stripeBillingDetails?.name || '';
    const existingCardExpiryDate = `${stripeCard?.exp_month}/${stripeCard?.exp_year}`;
    const cardBrand = stripeCard?.brand;
    const formatCardBrand = cardBrand === 'diners' ? 'dinersclub' : cardBrand;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // init
    useEffect(() => setIsRequestLoadCustomer(true), []);

    // load Stripe customer data
    useEffect(() => {
        const retrieveCustomer = async () => {
            const retrieveCustomerResp = await retrieveStripeCustomer({ stripeCusId });
            if (retrieveCustomerResp) {
                setStripeCustomer(retrieveCustomerResp.data);
                setIsLoading(false);
            }
            setIsRequestLoadCustomer(false);
        };
        if (stripeCusId && isRequestLoadCustomer) {
            retrieveCustomer();
        }
    }, [stripeCusId, isRequestLoadCustomer]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickUpdateCardLink = () => {
        setIsEditMode(true);
    };

    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeStripeCardNumber = field => {
        const complete = !!field?.complete;
        setStripeComplete(prev => ({ ...prev, cardNumber: complete }));

        const msg = field?.error ? field.error.message : null;
        setStripeErrorMessages(prev => ({ ...prev, cardNumber: msg }));
    };

    const handleChangeStripeCardExpiry = field => {
        const complete = !!field?.complete;
        setStripeComplete(prev => ({ ...prev, cardExpiry: complete }));

        const msg = field?.error ? field.error.message : null;
        setStripeErrorMessages(prev => ({ ...prev, cardExpiry: msg }));
    };

    const handleChangeStripeCardCvc = field => {
        const complete = !!field?.complete;
        setStripeComplete(prev => ({ ...prev, cardCvc: complete }));

        const msg = field?.error ? field.error.message : null;
        setStripeErrorMessages(prev => ({ ...prev, cardCvc: msg }));
    };

    const handleClickCancelButton = () => {
        setIsEditMode(false);
    };

    const handleClickSaveCardButton = async () => {
        setIsUpdatingCard(true);
        try {
            const resp = await createCardSetupIntent({ stripeCusId });
            // console.log(resp);
            //-------------------------------------------------------------------------------------------
            // NOTE: This Stripe API must be called from here for the 'card' param to work.
            //       Pass in CardNumberElement and Stripe.js SDK will automatically pull in
            //       CardExpiryElement and CardCvcElement.
            //-------------------------------------------------------------------------------------------
            if (resp && !resp?.error) {
                const clientSecret = resp.data?.client_secret;
                const cardHoldersName = data?.cardHoldersName;

                const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
                    payment_method: {
                        card: stripeElements.getElement(CardNumberElement),
                        billing_details: getConfirmCardSetupBillingDetails({ cardHoldersName }),
                        // billing_details: { address: { country: 'Hong Kong' } }, //! Use this to throw error
                    },
                });

                if (error) {
                    throw new Error('Failed to confirm card setup');
                } else {
                    const success = await saveEmployerSetupIntentCardPaymentMethod({ employerId, stripeCusId, setupIntent, setSnackbarMessage, setSnackbarErrorMessage });
                    if (success) {
                        setStripeCustomer(null);
                        setIsEditMode(false);
                        setIsRequestLoadCustomer(true);
                        setIsUpdatingCard(false);
                        setData({ ...data, cardHoldersName: '' });
                    } else {
                        throw new Error('Failed to save payment method for employer.');
                    }
                }
            } else {
                throw new Error('Failed to create Stripe setup intent');
            }
        } catch (error) {
            setIsUpdatingCard(false);
            //TODO Handle card setup error
            console.error(error.message);
            alert(error.message);
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles()();
    const defns = getDefns({
        classes,
        props,
        data,
        setData,
        stripeComplete,
        stripeErrorMessages,
        handleChangeFields,
        handleClickCancelButton,
        handleClickSaveCardButton,
        stripeBillingDetails,
        stripeCard,
        handleClickUpdateCardLink,
        handleChangeStripeCardNumber,
        handleChangeStripeCardExpiry,
        handleChangeStripeCardCvc,
        isUpdatingCard,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.container}>
            {isLoading && (
                <div {...defns.circularDiv}>
                    <CircularProgress size={40} />
                </div>
            )}

            {!isLoading && stripeCard && !isEditMode && (
                <div>
                    <Card name={existingCardHoldersName} number={`**** **** **** ${stripeCard?.last4}`} expiry={existingCardExpiryDate} preview={true} issuer={formatCardBrand} />
                    <div {...defns.buttonBox}>
                        <AfitdButton {...defns.updateCardLink}>Update Card</AfitdButton>
                    </div>
                </div>
            )}
            {!isLoading && (!stripeCard || isEditMode) && (
                <div {...defns.cardInfoContainer}>
                    <div {...defns.inputBox} style={{ marginTop: 0 }}>
                        <AfitdTypography {...defns.label}>Cardholder name</AfitdTypography>
                        <AfitdTextField {...defns.cardHoldersName} />
                    </div>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Card number</AfitdTypography>
                        {/* <AfitdNumberFormat {...defns.cardNumber} customInput={AfitdTextField} /> */}
                        <div {...defns.stripeCardElementDiv}>
                            <CardNumberElement
                                options={{
                                    showIcon: true,
                                    style: {
                                        base: {
                                            fontSize: '16px',
                                        },
                                    },
                                }}
                                {...defns.stripeCardNumber}
                            />
                        </div>
                        {stripeErrorMessages?.cardNumber && <AfitdTypography {...defns.stripeCardNumberErrorMessage} />}
                    </div>
                    <div {...defns.inputBoxContainer}>
                        <div {...defns.inputBox}>
                            <AfitdTypography {...defns.label}>Expiration Date</AfitdTypography>
                            {/* <AfitdNumberFormat {...defns.cardExpiryDate} customInput={AfitdTextField} format={checkCardExpiryDate} /> */}
                            <div {...defns.stripeCardElementDiv}>
                                <CardExpiryElement
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: '16px',
                                            },
                                        },
                                    }}
                                    {...defns.stripeCardExpiry}
                                />
                            </div>
                            {stripeErrorMessages?.cardExpiry && <AfitdTypography {...defns.stripeCardExpiryErrorMessage} />}
                        </div>
                        <div {...defns.inputBox}>
                            <AfitdTypography {...defns.label}>CVC Code</AfitdTypography>
                            {/* <AfitdNumberFormat {...defns.cardCVC} customInput={AfitdTextField} /> */}
                            <div {...defns.stripeCardElementDiv}>
                                <CardCvcElement
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: '16px',
                                            },
                                        },
                                    }}
                                    {...defns.stripeCardCvc}
                                />
                            </div>
                            {stripeErrorMessages?.cardCvc && <AfitdTypography {...defns.stripeCardCvcErrorMessage} />}
                        </div>
                    </div>
                </div>
            )}
            {!isLoading && (!stripeCard || isEditMode) && (
                <div {...defns.buttonBox}>
                    <AfitdButton {...defns.cancelButton} />
                    <AfitdButton {...defns.saveCardButton} />
                </div>
            )}
        </div>
    );
};

export default BillingInfo;
